import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import LandingNavigation from '../components/LandingNavigation';
import Hero from '../sections/Hero';
import StatsStrip from '../sections/StatsStrip';
import ProblemSolution from '../sections/ProblemSolution';
import AssetLibrary from '../sections/AssetLibrary';
import EntityProfiles from '../sections/EntityProfiles';
import AIGeneration from '../sections/AIGeneration';
import Workflow from '../sections/Workflow';
import Features from '../sections/Features';
import CTA from '../sections/CTA';

gsap.registerPlugin(ScrollTrigger);

export function LandingPage() {
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const pinned = ScrollTrigger.getAll()
        .filter((st) => (st.vars as { pin?: boolean }).pin)
        .sort((a, b) => a.start - b.start);
      const maxScroll = ScrollTrigger.maxScroll(window);
      if (!maxScroll || pinned.length === 0) return;

      const pinnedRanges = pinned.map((st) => {
        const end = st.end ?? st.start;
        return {
          start: st.start / maxScroll,
          end: end / maxScroll,
          center: (st.start + (end - st.start) * 0.5) / maxScroll,
        };
      });

      ScrollTrigger.create({
        snap: {
          snapTo: (value: number) => {
            const inPinned = pinnedRanges.some((r) => value >= r.start - 0.02 && value <= r.end + 0.02);
            if (!inPinned) return value;
            const target = pinnedRanges.reduce(
              (closest, r) => (Math.abs(r.center - value) < Math.abs(closest - value) ? r.center : closest),
              pinnedRanges[0]?.center ?? 0
            );
            return target;
          },
          duration: { min: 0.15, max: 0.35 },
          delay: 0,
          ease: 'power2.out',
        },
      });
    }, 500);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, []);

  return (
    <>
      <div className="grain-overlay" />
      <LandingNavigation />
      <main ref={mainRef} className="relative min-h-screen bg-[#0B0F10]">
        <Hero className="z-10" />
        <StatsStrip className="z-[15]" />
        <ProblemSolution className="z-20" />
        <AssetLibrary className="z-30" />
        <EntityProfiles className="z-40" />
        <AIGeneration className="z-50" />
        <Workflow className="z-[60]" />
        <Features className="z-[70]" />
        <CTA className="z-[80]" />
      </main>
    </>
  );
}
